import { claudeService } from './ai/claudeService'
import {
  MenuImage,
  ImageOptions,
  prepareMenuImages,
  totalBase64Size,
  DEFAULT_IMAGE_OPTIONS,
  SMALL_IMAGE_OPTIONS,
  MAX_MENU_PAGES,
} from './ai/menuImages'
import { Menu } from '../types/menu'

// Keep in sync with MAX_TOTAL_BASE64_CHARS in api/parse-menu.js (minus some headroom)
const MAX_UPLOAD_BASE64_CHARS = 3_800_000

export interface ProcessingProgress {
  stage: 'reading' | 'parsing' | 'complete'
  progress: number
  message: string
}

export interface MenuProcessingResult {
  menu: Menu
  confidence: number
  warnings: string[]
  tokensUsed?: { input: number; output: number }
  processedFiles: number
  consolidatedSections: number
  mergedSections: string[]
}

// Kept as an alias so existing imports keep working
export type MultiFileProcessingResult = MenuProcessingResult

function throwIfCancelled(signal?: AbortSignal) {
  if (signal?.aborted) {
    throw new Error('Processing was cancelled')
  }
}

export class MenuProcessorService {
  private progressCallback?: (progress: ProcessingProgress) => void

  setProgressCallback(callback: (progress: ProcessingProgress) => void) {
    this.progressCallback = callback
  }

  /**
   * Reads every uploaded file (images and PDF pages) and sends them to Claude
   * together, so multi-page menus come back as one combined menu.
   */
  async processMultipleMenuFiles(files: File[], abortSignal?: AbortSignal): Promise<MenuProcessingResult> {
    if (files.length === 0) {
      throw new Error('No files provided for processing')
    }

    this.updateProgress('reading', 10, 'Setting the table…')
    let images = await this.prepareAll(files, DEFAULT_IMAGE_OPTIONS)
    throwIfCancelled(abortSignal)

    if (images.length > MAX_MENU_PAGES) {
      throw new Error(`That's ${images.length} pages. Please upload ${MAX_MENU_PAGES} pages or fewer at a time.`)
    }

    if (totalBase64Size(images) > MAX_UPLOAD_BASE64_CHARS) {
      images = await this.prepareAll(files, SMALL_IMAGE_OPTIONS)
      throwIfCancelled(abortSignal)
      if (totalBase64Size(images) > MAX_UPLOAD_BASE64_CHARS) {
        throw new Error('Your menu files are too large. Please try fewer pages or smaller images.')
      }
    }

    this.updateProgress('parsing', 40, 'Organizing your spread…')
    const result = await claudeService.parseMenuImages(images, abortSignal)
    throwIfCancelled(abortSignal)

    this.updateProgress('complete', 100, 'Menu processing complete')

    const menu: Menu = {
      ...result.menu,
      metadata: {
        ...result.menu.metadata,
        originalFileName: files.map((f) => f.name).join(', '),
      },
    }

    return {
      menu,
      confidence: result.confidence,
      warnings: result.warnings,
      tokensUsed: result.tokensUsed,
      processedFiles: files.length,
      consolidatedSections: menu.sections.length,
      mergedSections: [],
    }
  }

  private async prepareAll(files: File[], options: ImageOptions): Promise<MenuImage[]> {
    const images: MenuImage[] = []
    for (const file of files) {
      try {
        images.push(...(await prepareMenuImages(file, options)))
      } catch (error) {
        if (error instanceof Error && error.message.includes('pages or fewer')) throw error
        console.error(`Could not read ${file.name}:`, error)
        throw new Error(`We couldn't read "${file.name}". Please check the file and try again.`, {
          cause: error,
        })
      }
    }
    return images
  }

  private updateProgress(stage: ProcessingProgress['stage'], progress: number, message: string) {
    this.progressCallback?.({ stage, progress, message })
  }
}

export const menuProcessor = new MenuProcessorService()
